import React, { useMemo, useState } from 'react';
import { Badge, DataTable } from '../Ui';
import { STAFF_ROLES } from '../../config/plans';
import { ROLES } from '../../permissions';
import { formatDoctorName, randomPassword, suggestStaffEmail } from '../../utils/staffLogin';
import { ipApi } from '../../services/api';

const LOGIN_URL = 'https://networks.hcen.in/login';

const emptyForm = {
  role: 'doctor',
  name: '',
  email: '',
  password: '',
  specialty: '',
  phone: '',
};

function CredentialCard({ creds, onClose }) {
  const text = `HCEN Networks login\nURL: ${LOGIN_URL}\nEmail: ${creds.email}\nPassword: ${creds.password}\nName: ${creds.name}\nRole: ${creds.roleLabel}`;
  return (
    <div className="credential-card card mb">
      <h3>Login created — share with staff</h3>
      <dl className="credential-dl">
        <dt>Name</dt><dd>{creds.name}</dd>
        <dt>Role</dt><dd>{creds.roleLabel}</dd>
        <dt>Email</dt><dd><code>{creds.email}</code></dd>
        <dt>Password</dt><dd><code>{creds.password}</code></dd>
      </dl>
      <div className="row-actions">
        <button type="button" className="btn btn-primary" onClick={() => navigator.clipboard?.writeText(text)}>Copy</button>
        <button type="button" className="btn btn-secondary" onClick={onClose}>Done</button>
      </div>
    </div>
  );
}

export default function PlatformStaffEditor({ hospitalId, hospital, quotas, staff, onReload, onMessage }) {
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [emailTouched, setEmailTouched] = useState(false);
  const [busy, setBusy] = useState('');
  const [editId, setEditId] = useState(null);
  const [editForm, setEditForm] = useState({
    name: '', role: '', email: '', active: true, specialty: '', phone: '', password: '',
  });
  const [createdCreds, setCreatedCreds] = useState(null);
  const [resetPwd, setResetPwd] = useState(null);
  const [roleFilter, setRoleFilter] = useState('all');

  const slug = hospital?.slug || 'hospital';

  const roleIndex = useMemo(() => {
    const counts = {};
    (staff || []).forEach((s) => { if (s.active !== false) counts[s.role] = (counts[s.role] || 0) + 1; });
    return (role) => (counts[role] || 0) + 1;
  }, [staff]);

  const quotaHint = (role) => {
    if (!quotas?.limits) return null;
    const limit = quotas.limits[role] ?? 0;
    const used = quotas.usage?.[role] ?? 0;
    if (limit <= 0) return 'No quota set';
    return `${used}/${limit} used${used >= limit ? ' — at limit (platform override allowed)' : ''}`;
  };

  const applySuggestedEmail = (role, name) => {
    if (emailTouched) return;
    const email = suggestStaffEmail({ role, name, slug, index: roleIndex(role) });
    setForm((f) => ({ ...f, email }));
  };

  const onNameChange = (name) => {
    let displayName = name;
    if (form.role === 'doctor' && name && !/^dr\.?\s/i.test(name)) {
      displayName = formatDoctorName(name.replace(/^dr\.?\s*/i, ''));
    }
    setForm((f) => ({ ...f, name: displayName }));
    applySuggestedEmail(form.role, displayName);
  };

  const createStaff = async (e) => {
    e.preventDefault();
    setBusy('create');
    onMessage?.('');
    try {
      const password = form.password || randomPassword();
      const payload = {
        name: form.name.trim(),
        email: form.email.toLowerCase().trim(),
        role: form.role,
        password,
        specialty: form.specialty?.trim() || undefined,
        phone: form.phone?.trim() || undefined,
      };
      const res = await ipApi.platformCreateStaff(hospitalId, payload);
      const finalPassword = res.temporaryPassword || password;
      setForm(emptyForm);
      setShowForm(false);
      setEmailTouched(false);
      setCreatedCreds({
        name: payload.name,
        email: payload.email,
        password: finalPassword,
        roleLabel: ROLES[form.role]?.label || form.role,
      });
      onMessage?.('Staff login created.');
      await onReload?.();
    } catch (err) {
      onMessage?.(err.message);
    } finally {
      setBusy('');
    }
  };

  const startEdit = (row) => {
    setEditId(row.staffId);
    setEditForm({
      name: row.name,
      role: row.role,
      email: row.email,
      active: row.active !== false,
      specialty: row.specialty || '',
      phone: row.phone || '',
      password: '',
    });
  };

  const saveEdit = async () => {
    setBusy(editId);
    onMessage?.('');
    try {
      const body = {
        name: editForm.name,
        role: editForm.role,
        email: editForm.email,
        active: editForm.active,
        specialty: editForm.specialty,
        phone: editForm.phone,
      };
      if (editForm.password) body.password = editForm.password;
      await ipApi.platformPatchStaff(hospitalId, editId, body);
      setEditId(null);
      onMessage?.('Staff updated.');
      await onReload?.();
    } catch (err) {
      onMessage?.(err.message);
    } finally {
      setBusy('');
    }
  };

  const resetPassword = async (row) => {
    if (!window.confirm(`Reset password for ${row.name}?`)) return;
    setBusy(`reset-${row.staffId}`);
    onMessage?.('');
    try {
      const res = await ipApi.platformResetStaffPassword(hospitalId, row.staffId);
      setResetPwd({ name: row.name, email: row.email, password: res.temporaryPassword });
      onMessage?.('Password reset.');
    } catch (err) {
      onMessage?.(err.message);
    } finally {
      setBusy('');
    }
  };

  const toggleActive = async (row) => {
    setBusy(row.staffId);
    try {
      await ipApi.platformPatchStaff(hospitalId, row.staffId, { active: row.active === false });
      await onReload?.();
    } catch (err) {
      onMessage?.(err.message);
    } finally {
      setBusy('');
    }
  };

  const filteredStaff = roleFilter === 'all'
    ? (staff || [])
    : (staff || []).filter((s) => s.role === roleFilter);

  const columns = [
    {
      key: 'name',
      label: 'Name',
      render: (r) => (
        <div>
          <strong>{r.name}</strong>
          {r.isHospitalOwner && <Badge tone="info">Owner</Badge>}
          {r.specialty && <div className="hint">{r.specialty}</div>}
        </div>
      ),
    },
    { key: 'email', label: 'Login email' },
    { key: 'role', label: 'Role', render: (r) => ROLES[r.role]?.label || r.role },
    {
      key: 'active',
      label: 'Status',
      render: (r) => (
        <Badge tone={r.active !== false ? 'success' : 'warning'}>
          {r.active !== false ? 'Active' : 'Disabled'}
        </Badge>
      ),
    },
    {
      key: 'actions',
      label: 'Actions',
      render: (r) => (
        <div className="row-actions">
          <button type="button" className="btn btn-sm btn-secondary" disabled={!!busy} onClick={() => startEdit(r)}>Edit</button>
          <button type="button" className="btn btn-sm btn-secondary" disabled={busy === `reset-${r.staffId}`} onClick={() => resetPassword(r)}>Reset pwd</button>
          <button type="button" className="btn btn-sm btn-secondary" disabled={busy === r.staffId} onClick={() => toggleActive(r)}>
            {r.active === false ? 'Enable' : 'Disable'}
          </button>
        </div>
      ),
    },
  ];

  return (
    <div>
      <div className="card mb row-actions" style={{ justifyContent: 'space-between', flexWrap: 'wrap', gap: '0.75rem' }}>
        <p className="hint">HCEN can add, edit, disable, and reset any hospital login.</p>
        <button type="button" className="btn btn-primary btn-sm" onClick={() => { setShowForm((s) => !s); setCreatedCreds(null); }}>
          {showForm ? 'Close form' : '+ Add staff login'}
        </button>
      </div>

      {createdCreds && <CredentialCard creds={createdCreds} onClose={() => setCreatedCreds(null)} />}
      {resetPwd && (
        <div className="owner-credentials card mb">
          <strong>New password for {resetPwd.name}:</strong> <code>{resetPwd.password}</code>
          <span className="hint"> · {resetPwd.email}</span>
        </div>
      )}

      {showForm && (
        <form className="card mb form-grid" onSubmit={createStaff}>
          <h3>Add staff login</h3>
          <div className="field full">
            <label>Role</label>
            <div className="role-chip-row">
              {STAFF_ROLES.map((r) => (
                <button
                  key={r.id}
                  type="button"
                  className={`emr-chip ${form.role === r.id ? 'active' : ''}`}
                  onClick={() => {
                    setForm((f) => ({ ...f, role: r.id }));
                    setEmailTouched(false);
                  }}
                >
                  {r.label}
                </button>
              ))}
            </div>
            <p className="hint" style={{ marginTop: 8 }}>Quota: {quotaHint(form.role)}</p>
          </div>
          <div className="field"><label>Name</label><input className="input" required value={form.name} onChange={(e) => onNameChange(e.target.value)} /></div>
          <div className="field">
            <label>Email</label>
            <input className="input" required type="email" value={form.email} onChange={(e) => { setEmailTouched(true); setForm({ ...form, email: e.target.value }); }} />
          </div>
          <div className="field"><label>Password (optional)</label><input className="input" value={form.password} placeholder="Auto-generated if blank" onChange={(e) => setForm({ ...form, password: e.target.value })} /></div>
          {form.role === 'doctor' && (
            <div className="field"><label>Specialty</label><input className="input" value={form.specialty} onChange={(e) => setForm({ ...form, specialty: e.target.value })} /></div>
          )}
          <div className="field"><label>Phone</label><input className="input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
          <div className="form-actions full">
            <button type="submit" className="btn btn-primary" disabled={busy === 'create'}>Create login</button>
          </div>
        </form>
      )}

      {editId && (
        <div className="card mb form-grid">
          <h3>Edit staff</h3>
          <div className="field"><label>Name</label><input className="input" value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} /></div>
          <div className="field"><label>Email</label><input className="input" value={editForm.email} onChange={(e) => setEditForm({ ...editForm, email: e.target.value })} /></div>
          <div className="field">
            <label>Role</label>
            <select className="input" value={editForm.role} onChange={(e) => setEditForm({ ...editForm, role: e.target.value })}>
              {STAFF_ROLES.map((r) => <option key={r.id} value={r.id}>{r.label}</option>)}
            </select>
          </div>
          <div className="field"><label>New password (optional)</label><input className="input" value={editForm.password} onChange={(e) => setEditForm({ ...editForm, password: e.target.value })} /></div>
          <div className="field"><label>Specialty</label><input className="input" value={editForm.specialty} onChange={(e) => setEditForm({ ...editForm, specialty: e.target.value })} /></div>
          <div className="field"><label>Phone</label><input className="input" value={editForm.phone} onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })} /></div>
          <div className="field">
            <label><input type="checkbox" checked={editForm.active} onChange={(e) => setEditForm({ ...editForm, active: e.target.checked })} /> Active</label>
          </div>
          <div className="form-actions full row-actions">
            <button type="button" className="btn btn-primary" disabled={busy === editId} onClick={saveEdit}>Save</button>
            <button type="button" className="btn btn-secondary" onClick={() => setEditId(null)}>Cancel</button>
          </div>
        </div>
      )}

      <div className="card">
        <div className="row-actions mb" style={{ flexWrap: 'wrap', gap: '0.5rem' }}>
          <span className="hint">Filter:</span>
          <button type="button" className={`emr-chip ${roleFilter === 'all' ? 'active' : ''}`} onClick={() => setRoleFilter('all')}>All ({staff?.length || 0})</button>
          {STAFF_ROLES.map((r) => {
            const n = (staff || []).filter((s) => s.role === r.id).length;
            if (!n) return null;
            return (
              <button key={r.id} type="button" className={`emr-chip ${roleFilter === r.id ? 'active' : ''}`} onClick={() => setRoleFilter(r.id)}>
                {r.label} ({n})
              </button>
            );
          })}
        </div>
        <DataTable columns={columns} rows={filteredStaff} keyField="staffId" emptyMessage="No staff yet." />
      </div>
    </div>
  );
}
